import React, { useCallback, useEffect, useState } from 'react';
import { Phone, MapPin, Globe, Mail, Clock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
type PrintMode = 'none' | 'front' | 'back';
/*
 * Vistaprint UK specs:
 * Trim:  85 × 55 mm
 * Bleed: 91 × 61 mm (3 mm each side)
 * Safe:  79 × 49 mm (3 mm inside trim)
 *
 * Screen preview uses a scale factor to show the card at a comfortable size.
 * Print output uses exact mm dimensions.
 */
const SCALE = 5.5; // 1mm ≈ 5.5px on screen
const BLEED_W = 91 * SCALE; // 500.5 → 501px
const BLEED_H = 61 * SCALE; // 335.5 → 336px
const TRIM_INSET = 3 * SCALE; // 16.5px
const SAFE_INSET = 6 * SCALE; // 33px (3mm bleed + 3mm safe)
export function BusinessCardPage() {
  const [printMode, setPrintMode] = useState<PrintMode>('none');
  const [showGuides, setShowGuides] = useState(true);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode !== 'none') {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode('none');
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  return <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center print:bg-white print:p-0 print:m-0" data-id="element-1077">
      <div className="max-w-7xl w-full space-y-10 print:space-y-0" data-id="element-1078">
        {/* Page title */}
        <div className="text-center print:hidden" data-id="element-1079">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-1080">
            Dentico Clinic Business Card
          </h1>
          <p className="mt-2 text-gray-600" data-id="element-1081">
            Vistaprint-ready · 91 × 61 mm (with 3 mm bleed)
          </p>
          <div className="mt-4 flex items-center justify-center gap-3" data-id="element-1082">
            <button onClick={() => setShowGuides(!showGuides)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-1083">
              {showGuides ? <Eye className="w-3.5 h-3.5" data-id="element-1084" /> : <EyeOff className="w-3.5 h-3.5" data-id="element-1085" />}
              {showGuides ? 'Guides On' : 'Guides Off'}
            </button>
            <div className="flex items-center gap-3 text-[10px] text-gray-400" data-id="element-1086">
              <span className="flex items-center gap-1" data-id="element-1087">
                <span className="w-3 h-0 border-t border-dashed border-red-400 inline-block" data-id="element-1088" />{' '}
                Trim
              </span>
              <span className="flex items-center gap-1" data-id="element-1089">
                <span className="w-3 h-0 border-t border-dashed border-blue-400 inline-block" data-id="element-1090" />{' '}
                Safe zone
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-20 print:block" data-id="element-1091">
          {/* ===== FRONT SIDE ===== */}
          <div className="flex flex-col items-center gap-3 print:mb-0" data-card="front" style={printMode === 'back' ? {
          display: 'none'
        } : undefined} data-id="element-1092">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden" data-id="element-1093">
              Front Side
            </span>
            <div className="card-bleed relative bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-200 print:shadow-none print:rounded-none print:border-0" style={{
            width: BLEED_W,
            height: BLEED_H
          }} data-id="element-1094">
              {/* Bleed-area accent bars — extend full width */}
              <div className="absolute top-0 left-0 w-full" style={{
              height: TRIM_INSET + 6,
              backgroundColor: '#0d9488'
            }} data-id="element-1095" />
              <div className="absolute bottom-0 left-0 w-full" style={{
              height: TRIM_INSET + 6,
              backgroundColor: '#0d9488'
            }} data-id="element-1096" />

              {/* Safe-zone content */}
              <div className="absolute flex flex-col items-center justify-center" style={{
              top: SAFE_INSET,
              left: SAFE_INSET,
              right: SAFE_INSET,
              bottom: SAFE_INSET
            }} data-id="element-1097">
                {/* Logo */}
                <div className="mb-5" data-id="element-1098">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: '#0d9488'
                }} data-id="element-1099">
                    <span className="text-white font-bold text-4xl" data-id="element-1100">D</span>
                  </div>
                </div>

                {/* Brand */}
                <h2 className="text-3xl font-bold text-gray-900 tracking-tight" data-id="element-1101">
                  Dentico
                  <span style={{
                  color: '#0d9488'
                }} data-id="element-1102">
                    Clinic
                  </span>
                </h2>
                <div className="w-10 h-px mx-auto my-3 rounded-full" style={{
                backgroundColor: '#0d9488',
                opacity: 0.7
              }} data-id="element-1103" />
                <p className="text-gray-500 font-medium text-xs tracking-[0.06em] uppercase text-center" data-id="element-1104">
                  Українська стоматологічна клініка в Лондоні
                </p>
              </div>

              {/* Guides overlay */}
              {showGuides && <GuidesOverlay data-id="element-1105" />}
            </div>
          </div>

          {/* ===== BACK SIDE ===== */}
          <div className="flex flex-col items-center gap-3 print:mb-0" data-card="back" style={printMode === 'front' ? {
          display: 'none'
        } : undefined} data-id="element-1106">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden" data-id="element-1107">
              Back Side
            </span>
            <div className="card-bleed relative bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-200 print:shadow-none print:rounded-none print:border-0" style={{
            width: BLEED_W,
            height: BLEED_H
          }} data-id="element-1108">
              {/* Safe-zone content */}
              <div className="absolute flex flex-col" style={{
              top: SAFE_INSET,
              left: SAFE_INSET,
              right: SAFE_INSET,
              bottom: SAFE_INSET
            }} data-id="element-1109">
                {/* Header */}
                <div className="flex justify-between items-center border-b border-gray-100 pb-2.5 mb-3" data-id="element-1110">
                  <div data-id="element-1111">
                    <h3 className="text-base font-bold text-gray-900 leading-tight" data-id="element-1112">
                      Dentico
                      <span style={{
                      color: '#0d9488'
                    }} data-id="element-1113">
                        Clinic
                      </span>
                    </h3>
                    <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-1114">
                      Українська стоматологічна клініка в Лондоні
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{
                  backgroundColor: '#0d9488'
                }} data-id="element-1115">
                    <span className="text-white font-bold text-sm" data-id="element-1116">D</span>
                  </div>
                </div>

                {/* Contacts + QR */}
                <div className="flex-1 flex justify-between gap-4" data-id="element-1117">
                  <div className="flex flex-col gap-[9px] justify-center" data-id="element-1118">
                    <div className="flex items-center gap-2.5" data-id="element-1119">
                      <Phone className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-1120" />
                      <span className="text-[13px] font-semibold text-gray-800" data-id="element-1121">
                        +44 7345 197820
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-1122">
                      <Globe className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-1123" />
                      <span className="text-[12px] font-medium text-gray-700" data-id="element-1124">
                        denticoclinic.co.uk
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-1125">
                      <Mail className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-1126" />
                      <span className="text-[12px] font-semibold text-gray-800" data-id="element-1127">
                        info@denticoclinic.co.uk
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-1128">
                      <MapPin className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-1129" />
                      <span className="text-[11px]" style={{
                      color: '#374151'
                    }} data-id="element-1130">
                        360–364 City Rd, London EC1V 2PY
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-1131">
                      <Clock className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-1132" />
                      <span className="text-[11px]" style={{
                      color: '#374151'
                    }} data-id="element-1133">
                        Mon–Sat 10:00–18:00
                      </span>
                    </div>
                  </div>

                  {/* QR */}
                  <div className="flex flex-col items-center justify-center" data-id="element-1134">
                    <div className="w-[72px] h-[72px] rounded overflow-hidden" data-id="element-1135">
                      <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://wa.me/447345197820" alt="WhatsApp QR code" className="w-full h-full object-contain" data-id="element-1136" />
                    </div>
                    <span className="text-[7px] mt-1 uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-1137">
                      WhatsApp Me
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-2 border-t border-gray-100 mt-auto" data-id="element-1138">
                  <p className="text-[9px] leading-relaxed" style={{
                  color: '#6b7280'
                }} data-id="element-1139">
                    Appointments available online or by phone.
                  </p>
                </div>
              </div>

              {/* Guides overlay */}
              {showGuides && <GuidesOverlay data-id="element-1140" />}
            </div>
          </div>
        </div>

        {/* Buttons + specs */}
        <div className="flex flex-col items-center gap-5 pt-6 print:hidden" data-id="element-1141">
          <div className="flex gap-4" data-id="element-1142">
            <Button onClick={() => handlePrint('front')} size="lg" className="shadow-lg hover:shadow-xl transition-all" data-id="element-1143">
              Save Front as PDF
            </Button>
            <Button onClick={() => handlePrint('back')} size="lg" className="shadow-lg hover:shadow-xl transition-all" data-id="element-1144">
              Save Back as PDF
            </Button>
          </div>

          {/* Vistaprint spec reminder */}
          <div className="bg-white border border-gray-200 rounded-lg px-6 py-4 max-w-md text-center shadow-sm" data-id="element-1145">
            <p className="text-xs font-semibold text-gray-700 mb-2" data-id="element-1146">
              Vistaprint Print Settings
            </p>
            <div className="text-[11px] text-gray-500 space-y-1 leading-relaxed" data-id="element-1147">
              <p data-id="element-1148">
                In the print dialog, select <strong data-id="element-1149">"Save as PDF"</strong>
              </p>
              <p data-id="element-1150">
                Paper size: <strong data-id="element-1151">Custom — 91 × 61 mm</strong>
              </p>
              <p data-id="element-1152">
                Margins: <strong data-id="element-1153">None</strong>
              </p>
              <p data-id="element-1154">
                Scale: <strong data-id="element-1155">100%</strong>
              </p>
              <p className="pt-1 text-gray-400" data-id="element-1156">
                Upload both PDFs to Vistaprint as front & back
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles — exact Vistaprint dimensions */}
      <style data-id="element-1157">{`
        @media print {
          @page {
            size: 91mm 61mm;
            margin: 0;
          }

          /* Force color printing */
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Page background */
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }

          /* Hide everything except the card */
          h1, button, nav, header, footer,
          .print\\:hidden, [class*="print:hidden"] {
            display: none !important;
          }

          /* Strip layout from wrapper divs (not card internals) */
          .min-h-screen {
            display: block !important;
            min-height: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
          }

          .max-w-7xl {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          /* The card — keep pixel size, scale to fit 91×61mm */
          .card-bleed {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: ${BLEED_W}px !important;
            height: ${BLEED_H}px !important;
            border-radius: 0 !important;
            border: none !important;
            box-shadow: none !important;
            overflow: hidden !important;
            transform-origin: top left !important;
            transform: scale(calc(91mm / ${BLEED_W}px), calc(61mm / ${BLEED_H}px)) !important;
          }

          /* Hide guide overlays */
          .card-bleed .pointer-events-none {
            display: none !important;
          }
        }
      `}</style>
    </div>;
}
/* Trim + safe zone guide lines (screen only) */
function GuidesOverlay() {
  return <div className="absolute inset-0 pointer-events-none print:hidden" data-id="element-1158">
      {/* Trim line (red dashed) — 3mm from edge */}
      <div className="absolute border border-dashed" style={{
      top: TRIM_INSET,
      left: TRIM_INSET,
      right: TRIM_INSET,
      bottom: TRIM_INSET,
      borderColor: 'rgba(239, 68, 68, 0.45)'
    }} data-id="element-1159" />
      {/* Safe zone (blue dashed) — 6mm from edge */}
      <div className="absolute border border-dashed" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET,
      borderColor: 'rgba(59, 130, 246, 0.35)'
    }} data-id="element-1160" />
    </div>;
}